import React from "react";
import AppChip, { AppStatusChip } from "../../../../components/AppChip";
import AppFilterChip from "../../../../components/AppFilterChip";
import AppMetaChip from "../../../../components/AppMetaChip";
import DocsNiceToKnow from "../../shared/DocsNiceToKnow";
import DocsPreviewPanel from "../../shared/DocsPreviewPanel";
import DocsSectionCard from "../../shared/DocsSectionCard";

const appChipColumns = [
  { label: "Primary / Outlined", variant: "primary", appearance: "outlined", value: "MaMu" },
  { label: "Primary / SemiFilled", variant: "primary", appearance: "semiFilled", value: "B102" },
  { label: "Neutral / Outlined", variant: "neutral", appearance: "outlined", value: "1/4" },
  { label: "Neutral / SemiFilled", variant: "neutral", appearance: "semiFilled", value: "90 %" },
  { label: "Muted / Outlined", variant: "muted", appearance: "outlined", value: "7A" },
  { label: "Muted / SemiFilled", variant: "muted", appearance: "semiFilled", value: "2 offen" },
];

const appChipSizes = ["sm", "md", "lg"];

const metaChipSizes = ["xs", "sm", "md"];

const statusChipColumns = [
  { label: "Error", status: "error", value: "Fehler" },
  { label: "Warning", status: "warning", value: "Offen" },
  { label: "Success", status: "success", value: "Fertig" },
  { label: "Info", status: "info", value: "Entwurf" },
];

const statusChipSizes = ["sm", "md", "lg"];

const filterChipColumns = [
  { label: "Default", value: "Alle", count: 32 },
  { label: "Selected", value: "Klassen", count: 18, selected: true },
  { label: "Disabled", value: "Spezial", count: 2, disabled: true },
];

const filterChipSizes = ["sm", "md", "lg"];

const appChipNotes = [
  "Normale Marker ohne Status-Semantik.",
  "Varianten entstehen aus Variant + Appearance.",
];

const appChipHighlights = ["Icon-Slots: leadingIcon und trailingIcon."];

const metaChipNotes = [
  "Für sehr kurze Marker wie Schrittzahlen, Counter oder Pflicht.",
  "Nur kurze Inhalte verwenden.",
  "Aktiv genutzt: XS und SM. MD existiert im DS.",
];

const metaChipHighlights = ["Keine Icons, keine States, nur kompakter Wert."];

const statusChipNotes = [
  "Semantischer Status für Error, Warning, Success und Info.",
];

const statusChipHighlights = ["Status-Icon kommt automatisch; showIcon=false blendet es aus."];

const filterChipNotes = [
  "Interaktiver Filter-Button, nicht nur visueller Chip.",
  "Disabled kommt über die normale Button-Logik.",
];

const filterChipHighlights = ["Selected-State und optionaler Count sind eingebaut."];

function ChipSection({ title, notes, highlights = [], children }) {
  return (
    <div className="ds-docs-chip-section-grid">
      <DocsPreviewPanel title={title}>
        <div className="ds-docs-preview-surface ds-docs-chip-family-preview">{children}</div>
      </DocsPreviewPanel>
      <DocsNiceToKnow items={notes} highlightItems={highlights} />
    </div>
  );
}

function ChipMatrix({ columns, children }) {
  return (
    <div className="ds-docs-chip-matrix" style={{ "--ds-docs-chip-columns": columns.length }}>
      <div className="ds-docs-chip-matrix__header">
        <div className="ds-docs-chip-matrix__size-label">Size</div>
        {columns.map((column) => (
          <MatrixColumnLabel key={column.label} label={column.label} />
        ))}
      </div>
      {children}
    </div>
  );
}

function MatrixColumnLabel({ label }) {
  const parts = label.split(" / ");

  return (
    <div className="ds-docs-chip-matrix__column-label">
      {parts.map((part) => (
        <span key={part}>{part}</span>
      ))}
    </div>
  );
}

function MatrixRow({ size, children }) {
  return (
    <div className="ds-docs-chip-matrix__row">
      <div className="ds-docs-chip-matrix__size-label">{size.toUpperCase()}</div>
      {children}
    </div>
  );
}

function MatrixCell({ children }) {
  return <div className="ds-docs-chip-matrix__cell">{children}</div>;
}

function MetaChipRows() {
  return (
    <div className="ds-docs-chip-meta-rows">
      <div className="ds-docs-chip-meta-header">
        <div className="ds-docs-chip-matrix__size-label">Size</div>
        <MatrixColumnLabel label="Beispiele" />
      </div>
      {metaChipSizes.map((size) => (
        <div key={size} className="ds-docs-chip-meta-row">
          <div className="ds-docs-chip-matrix__size-label">{size.toUpperCase()}</div>
          <div className="ds-docs-chip-meta-row__content">
            <AppMetaChip size={size}>1</AppMetaChip>
            <AppMetaChip size={size}>Pflicht</AppMetaChip>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ChipsDocs() {
  return (
    <DocsSectionCard title="Chips">
      <div className="ds-docs-chips">
        <ChipSection title="AppChip" notes={appChipNotes} highlights={appChipHighlights}>
          <ChipMatrix columns={appChipColumns}>
            {appChipSizes.map((size) => (
              <MatrixRow key={size} size={size}>
                {appChipColumns.map((column) => (
                  <MatrixCell key={`${size}-${column.label}`}>
                    <AppChip variant={column.variant} appearance={column.appearance} size={size}>
                      {column.value}
                    </AppChip>
                  </MatrixCell>
                ))}
              </MatrixRow>
            ))}
          </ChipMatrix>
        </ChipSection>

        <ChipSection title="AppMetaChip" notes={metaChipNotes} highlights={metaChipHighlights}>
          <MetaChipRows />
        </ChipSection>

        <ChipSection title="AppStatusChip" notes={statusChipNotes} highlights={statusChipHighlights}>
          <ChipMatrix columns={statusChipColumns}>
            {statusChipSizes.map((size) => (
              <MatrixRow key={size} size={size}>
                {statusChipColumns.map((column) => (
                  <MatrixCell key={`${size}-${column.status}`}>
                    <AppStatusChip status={column.status} size={size}>
                      {column.value}
                    </AppStatusChip>
                  </MatrixCell>
                ))}
              </MatrixRow>
            ))}
          </ChipMatrix>
        </ChipSection>

        <ChipSection title="AppFilterChip" notes={filterChipNotes} highlights={filterChipHighlights}>
          <ChipMatrix columns={filterChipColumns}>
            {filterChipSizes.map((size) => (
              <MatrixRow key={size} size={size}>
                {filterChipColumns.map((column) => (
                  <MatrixCell key={`${size}-${column.label}`}>
                    <AppFilterChip
                      size={size}
                      count={column.count}
                      selected={column.selected}
                      disabled={column.disabled}
                    >
                      {column.value}
                    </AppFilterChip>
                  </MatrixCell>
                ))}
              </MatrixRow>
            ))}
          </ChipMatrix>
        </ChipSection>
      </div>
    </DocsSectionCard>
  );
}
